import React from "react";
import { Building2 } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * A client's brand mark, with the same footprint whether we have one or not —
 * so a table of 250 clients doesn't jump around between rows that have a logo
 * and rows that don't.
 *
 * Why `variant` matters
 * ---------------------
 * Every branded mark in the studio pack is the same flat slate ink (#54585D)
 * on transparency, drawn for a light page. Dropped straight onto the portal's
 * dark surface the sparser ones disappear — a wordmark with 15% coverage reads
 * as an empty box. Since they are monochrome, inverting them in the dark theme
 * is exactly right: the ink comes back as light grey and the transparency is
 * untouched.
 *
 * The plain originals must never be inverted — they are full colour on a white
 * card, and inverting one turns it into a photo negative.
 *
 * Why `onDark` exists on top of that
 * ----------------------------------
 * Theme-driven inversion is wrong wherever the backdrop is dark *regardless of
 * theme* — a portfolio tile draws its caption over a photo under a black
 * gradient, and in the light theme the mark would stay dark ink on near-black.
 * `onDark` says "this backdrop is always dark", so a branded mark is always
 * flipped to light there, matching the white caption beside it.
 */
export default function ClientLogo({
  src,
  name,
  variant = "branded",
  onDark = false,
  className,
  iconClassName,
}) {
  const branded = variant === "branded";
  return (
    <div
      className={cn(
        "shrink-0 rounded-lg bg-zinc-800/60 border border-zinc-700/50 flex items-center justify-center overflow-hidden",
        className || "w-10 h-10"
      )}
      title={name || undefined}
    >
      {src ? (
        <img
          src={src}
          alt={name || "logo"}
          loading="lazy"
          className={cn(
            "w-full h-full object-contain p-1",
            branded && (onDark ? "invert" : "dark:invert")
          )}
        />
      ) : (
        <Building2 className={cn("text-sky-400", iconClassName || "h-5 w-5")} />
      )}
    </div>
  );
}
